import * as _ from 'lodash';
import * as React from 'react';
import {
  InputStrengthIndicatorOverlay,
  InputValidationIndicatorOverlay,
  InputValidationStatus,
  passwordCreationUtils,
  TextInputGroup,
} from '@blend/consumer-components';
import { getIdFromName } from 'login-components/Common/Inputs/inputUtils';
import { BlIcon } from '@blend/shared-react';
import * as inputUtils from '../inputUtils';
import InputContainer from '../InputContainer/InputContainer';

interface PasswordInputProps {
  showStrengthIndicator?: boolean;
}

class PasswordInput extends React.Component<InputProps & PasswordInputProps, {}> {
  private inputWrapperRef = React.createRef<HTMLDivElement>();

  render() {
    const { input, placeholder, meta, hidePlaceholder, showStrengthIndicator, disabled, label } = this.props;
    const name = input.name;
    const identifier = placeholder || 'Password';
    const passwordStatuses: InputValidationStatus[] = passwordCreationUtils.getValidityStatus(input.value);
    const passwordStrength = passwordCreationUtils.getPasswordStrength(input.value, passwordStatuses);
    const passwordValid = passwordCreationUtils.allStatesValid(passwordStatuses);
    const error = meta.touched && meta.error;
    const passwordStrengthId = 'it-password-strength-popover';
    const passwordMatchId = 'it-password-match-popover';

    // We're only including the ids for the errored properties.
    const ariaProps = inputUtils.getAriaProps(
      name,
      !!error,
      _.compact([passwordValid && passwordStrengthId, error && passwordMatchId]),
    );

    let inputStatus;
    if (!meta.active || !showStrengthIndicator) {
      inputStatus = null;
    } else {
      inputStatus = passwordValid ? 'match' : 'mismatch';
    }

    return (
      <div className="PasswordInput">
        <InputContainer
          name={name}
          error={error}
          status={inputStatus}
          active={meta.active}
          identifier={identifier}
          label={label}
        >
          <div ref={this.inputWrapperRef} className="PasswordInput-inputWrapper">
            <TextInputGroup
              inputPrefix={
                <div className="Input-iconWrapper">
                  <BlIcon icon="lock-3" />
                </div>
              }
              prefixSize="md"
              type="password"
              {...input}
              {...ariaProps}
              aria-invalid={!!error}
              autoComplete="off"
              disabled={disabled}
              hasError={error}
              htmlId={getIdFromName(name)}
              name={name}
              placeholder={hidePlaceholder ? null : placeholder || identifier}
              required
            />
          </div>
          {showStrengthIndicator && (
            <div data-it="PasswordInput-strengthValidationOverlay">
              <InputStrengthIndicatorOverlay
                show={meta.active}
                strength={passwordStrength}
                inputWrapper={this.inputWrapperRef.current}
                indicatorId="it-password-strength-indicator"
              />
              <InputValidationIndicatorOverlay
                show={meta.active && !passwordValid}
                inputWrapper={this.inputWrapperRef.current}
                statuses={passwordStatuses}
                header="Your password must include:"
                indicatorId={passwordStrengthId}
              />
            </div>
          )}
        </InputContainer>
      </div>
    );
  }
}

export const PasswordInputConfig = {
  component: PasswordInput,
};

export const PasswordInputWithStrengthConfig = {
  component: props => <PasswordInput showStrengthIndicator {...props} />,
};

export default PasswordInput;
